import React, { ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  BusinessOutlined,
  PeopleOutline,
  TuneOutlined,
  CableOutlined,
  PersonOutline,
  SearchRounded,
  ArrowForwardRounded,
} from "@mui/icons-material";
import { selectPersonalGeneral } from "Redux/CompanyProps/slice";
import { useSelector } from "react-redux";
import {
  settingsAreas,
  getSettingForPath,
  SettingsArea,
} from "../SettingsHub/settingsCatalog";
import { useSettingsAccess } from "../SettingsHub/useSettingsAccess";
import styles from "./SettingsLayout.module.scss";
import legacyStyles from "./SettingsLegacy.module.scss";
const icons = {
  company: BusinessOutlined,
  access: PeopleOutline,
  operations: TuneOutlined,
  integrations: CableOutlined,
  account: PersonOutline,
};
const SettingsLayout = ({ children }: { children?: ReactNode }) => {
  const location = useLocation();
  const [query, setQuery] = useState("");
  const company = useSelector(selectPersonalGeneral);
  const { entries, loading, identity } = useSettingsAccess();
  const current = getSettingForPath(location.pathname);
  const legacyContent =
    [
      "/settings/general-setting/edit",
      "/settings/general-setting/billing",
      "/settings/user-profile/edit",
      "/settings/part-locations",
    ].includes(location.pathname.replace(/\/+$/, "")) ||
    location.pathname.startsWith("/settings/connections/samsara") ||
    location.pathname.startsWith("/settings/users/");
  const activeArea: SettingsArea =
    current?.area || entries[0]?.area || "account";
  const matches = entries.filter((e) =>
    `${e.label} ${e.description}`
      .toLowerCase()
      .includes(query.trim().toLowerCase()),
  );
  const currentAllowed =
    (!current || entries.some((e) => e.href === current.href)) &&
    (!location.pathname.replace(/\/+$/, "").endsWith("/general-setting/edit") ||
      identity.owner);
  useEffect(() => {
    setQuery("");
  }, [location.pathname]);
  return (
    <section className={styles.layout} aria-label="Settings" data-settings>
      <header className={styles.header}>
        <div>
          <h1>Settings</h1>
          <p>
            {company?.account_name || "Your workspace"}
            <span>Manage your company and fleet preferences.</span>
          </p>
        </div>
        <label className={styles.search}>
          <SearchRounded aria-hidden="true" />
          <input
            type="search"
            aria-label="Find a setting"
            placeholder="Find a setting…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setQuery("");
            }}
          />
        </label>
      </header>
      <nav className={styles.tabs} aria-label="Settings categories">
        {settingsAreas
          .filter((a) => entries.some((e) => e.area === a.id))
          .map((area) => {
            const Icon = icons[area.id];
            const target = entries.find((e) => e.area === area.id);
            if (!target) return null;
            return (
              <NavLink
                key={area.id}
                to={target.href}
                className={`${styles.tab} ${activeArea === area.id ? styles.active : ""}`}
                aria-current={activeArea === area.id ? "page" : undefined}
              >
                <Icon aria-hidden="true" />
                {area.label}
              </NavLink>
            );
          })}
      </nav>
      {loading ? (
        <div className={styles.message} role="status">
          Loading your settings access…
        </div>
      ) : (
        <>
          {query.trim() && (
            <div className={styles.results} aria-live="polite">
              <h2>Settings matching “{query.trim()}”</h2>
              {matches.length ? (
                matches.map((e) => (
                  <NavLink className={styles.result} key={e.href} to={e.href}>
                    <div>
                      <strong>{e.label}</strong>
                      <p>{e.description}</p>
                    </div>
                    <ArrowForwardRounded aria-hidden="true" />
                  </NavLink>
                ))
              ) : (
                <p>No matching settings. Try “tax”, “roles” or “reminders”.</p>
              )}
            </div>
          )}
          {!currentAllowed ? (
            <div className={styles.message} role="alert">
              <h2>This setting needs additional access</h2>
              <p>
                Ask your company owner to update your role. Your available
                settings are shown above.
              </p>
            </div>
          ) : (
            <div hidden={!!query.trim()}>
              <nav className={styles.subnav} aria-label="Settings pages">
                {entries
                  .filter((e) => e.area === activeArea && !e.secondary)
                  .map((e) => (
                    <NavLink
                      key={e.href}
                      to={e.href}
                      className={
                        current?.href === e.href ? styles.selected : ""
                      }
                      aria-current={
                        current?.href === e.href ? "page" : undefined
                      }
                    >
                      {e.label}
                    </NavLink>
                  ))}
              </nav>
              <div
                className={`${styles.content} ${legacyContent ? legacyStyles.legacy : ""}`}
                data-settings-legacy={legacyContent || undefined}
              >
                {children}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};
export default SettingsLayout;
